import { Entities, Formula, FormulasApi } from '@/api';
import { ApiUtils } from '@/common/ApiUtils';
import { FormulaInput } from './models/formula-input';

export class FormulasService {
	public formulas: Formula[] = [];
	public formulasMostUsed: Formula[] = [];
	public errorMessage: string | null = null;
	public loading: boolean = true;

	public get formulaOptions(): { text: string; value: number; disabled: boolean }[] {
		var mostUsed = this.formulasMostUsed
			.filter((formula: Formula) => formula.archived == false)
			.map((formula: Formula) => {
				return {
					text: formula.name + ' | used by ' + formula.usedBy + ' ' + this.entityType,
					value: formula.id,
					disabled: false,
				};
			});

		mostUsed = mostUsed.concat([
			{
				text:
					'\u2924 ' +
					mostUsed.length +
					' Most Used\u00A0\u00A0\u00A0|\u00A0\u00A0\u00A0Full List \u2926',
				value: 0,
				disabled: true,
			},
		]);

		const fullList = this.formulas
			.filter((formula: Formula) => formula.archived == false)
			.map((formula: Formula) => {
				return {
					text: formula.name + ' | used by ' + formula.usedBy + ' ' + this.entityType,
					value: formula.id,
					disabled: false,
				};
			});

		return mostUsed.concat(
			fullList.sort((a, b) => {
				if (a.text < b.text) {
					return -1;
				}
				if (a.text > b.text) {
					return 1;
				}
				return 0;
			}),
		);
	}

	private DROPDOWN_INPUT_TYPE = 'S';

	constructor(private entityType: Entities, private formulasApi = new FormulasApi()) {}

	public async initalise(): Promise<void> {
		this.formulas = [];
		this.formulasMostUsed = [];

		try {
			const formulasMostUsedResponse = await this.formulasApi.getFormulasForEntityMostUsed(
				this.entityType,
			);
			const formulasMostUsed = formulasMostUsedResponse.data.data;
			if (!formulasMostUsed || formulasMostUsed.length === 0) {
				this.errorMessage = 'No formulas to display';
			} else {
				this.errorMessage = null;
				this.formulasMostUsed = formulasMostUsed;
			}

			const formulasResponse = await this.formulasApi.getFormulasForEntity(this.entityType, {
				params: { sort: 'name', perPage: 500 },
			});
			const formulas = formulasResponse.data.data;
			if (!formulas || formulas.length === 0) {
				this.errorMessage = 'No formulas to display';
			} else {
				this.errorMessage = null;
				this.formulas = formulas;
			}
		} catch (e) {
			const errorResponse = ApiUtils.handleApiError(e, 'Failed to load formulas');
			this.errorMessage = errorResponse.errorMessage;
		} finally {
			this.loading = false;
		}
	}

	public getFormulaInputs(formulaId: number): FormulaInput[] | undefined {
		const formula = this.formulas.find((formula: Formula) => formula.id === formulaId);
		if (!formula) {
			this.errorMessage = 'Unknown formula selected';
			return;
		} else {
			this.errorMessage = null;
		}
		return this.formatFormulaInputs(formula);
	}

	private formatFormulaInputs(
		formula: Formula,
	): ReturnType<typeof this.getFormulaInputs> | undefined {
		if (!formula.inputs) {
			this.errorMessage = 'Selected formula has no inputs to configure';
			return;
		}
		// Inputs stored as pipe-separated type defs with new lines between each.
		const inputs: string[][] = formula.inputs
			.replace(/(\r\n|\n|\r)/g, '\\n')
			.split('\\n')
			.map((inputString: string) => inputString.split('|'));

		return inputs.map((input: string[]) => {
			const [type, name, label, possibleValues] = input;
			return {
				type,
				name,
				label,
				dropdownValues: type === this.DROPDOWN_INPUT_TYPE ? possibleValues.split(',') : null,
				unit:
					type !== this.DROPDOWN_INPUT_TYPE
						? possibleValues // 4th section is either a string seperated list of dropdown values, or the physical unit.
						: null,
				value: null,
				valid: null,
			};
		});
	}
}
